import { ComponentProps, useEffect } from 'react';

interface GoogleAdProps extends ComponentProps<'div'> {
    slotId?: string;
    format?: 'auto' | 'fluid' | 'rectangle';
    layout?: 'grid' | 'display' | 'in-article';
    label?: string;
    devMode?: boolean;
}

export function GoogleAd({
    slotId,
    format,
    layout = 'grid',
    label = 'Sponsored',
    devMode = false,
    className = ''
}: GoogleAdProps) {

    // Base classes that match your existing design system
    const baseClasses = "bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col justify-between";

    // Layout specific heights
    let heightClass = '';
    switch (layout) {
        case 'grid':
            heightClass = 'min-h-[280px] h-full';
            break;
        case 'display':
        default:
            heightClass = 'min-h-[250px] w-full';
            break;
    }

    useEffect(() => {
        if (devMode || !slotId) return;

        let cancelled = false;
        let idleHandle: number | undefined;
        let timeoutHandle: number | undefined;

        const loadAdsense = () => {
            if (cancelled) return;

            const pushAd = () => {
                if (cancelled) return;
                try {
                    const adsWindow = window as typeof window & { adsbygoogle?: unknown[] };
                    (adsWindow.adsbygoogle = adsWindow.adsbygoogle || []).push({});
                } catch (err) {
                    console.error('Google Ads error:', err);
                }
            };

            const existingScript = document.querySelector<HTMLScriptElement>('script[data-power-adsense]');
            if (existingScript) {
                if (existingScript.dataset.loaded === 'true') {
                    pushAd();
                } else {
                    existingScript.addEventListener('load', pushAd, { once: true });
                }
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.dataset.powerAdsense = 'true';
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8177723553694037';
            script.addEventListener('load', () => {
                script.dataset.loaded = 'true';
                pushAd();
            }, { once: true });
            document.head.appendChild(script);
        };

        if ('requestIdleCallback' in window) {
            idleHandle = window.requestIdleCallback(loadAdsense, { timeout: 2000 });
        } else {
            timeoutHandle = window.setTimeout(loadAdsense, 1000);
        }

        return () => {
            cancelled = true;
            if (idleHandle !== undefined && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleHandle);
            }
            if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle);
        };
    }, [devMode, slotId]);

    return (
        <div className={`${baseClasses} ${heightClass} ${className}`}>
            {/* Label Area */}
            <div className="px-4 pt-3 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">
                    {label}
                </span>
            </div>

            {/* Content Area */}
            <div className="relative flex-1 flex items-center justify-center p-4 bg-gray-50/50 m-2 rounded-lg border border-dashed border-gray-200">
                {/* Fallback Placeholder - Always rendered in background */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                    <div className="text-center pointer-events-auto">
                        <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-400">Ad</span>
                        </div>
                        <p className="text-xs text-gray-400 font-medium px-4">
                            Advertisement Space
                            <br />
                            <span className="text-[10px] opacity-75">
                                (
                                <a
                                    href="mailto:hello@power.ng?subject=Ad%20Space%20Inquiry"
                                    className="underline hover:text-gray-300 transition-colors"
                                >
                                    Email us
                                </a>
                                {' '}for Ad Space)
                            </span>
                        </p>
                    </div>
                </div>

                {/* Google Ad Layer - Overlays the placeholder */}
                {!devMode && (
                    <ins className="adsbygoogle relative z-10 w-full h-full block"
                        style={{ display: 'block' }}
                        data-ad-client="ca-pub-8177723553694037"
                        data-ad-slot={slotId}
                        data-ad-format={format || 'auto'}
                        data-full-width-responsive="true" />
                )}
            </div>

            {/* Footer Area - mimics tool card footer if needed, or simple branding */}
            <div className="px-4 pb-3">
                <div className="h-1 w-1/3 bg-gray-100 rounded-full"></div>
            </div>
        </div>
    );
}
